import * as React from 'react';
import { atom, useRecoilState } from 'recoil';

import { createSectionIDs, type SectionIDs } from '@gitbook/doc-core';
import { type Block, TextRange, type Value } from '@gitbook/slate';
import { useEditor } from '@gitbook/slate-react';
import { localStorage } from '@gitbook/util-local-storage';
import { useStateWithDeps } from '@gitbook/util-react-hooks';

import { useURLHashSectionId } from '../common/useURLHashSectionId';
import { EditorSectionIDsContext } from '../contexts';

interface TabsState {
    activeKeys: {
        [tabsBlockKey: string]: string;
    };
    activeTitles: string[];
}

// How many titles are remembered:
const TITLES_MAX = 5;

const defaultTabsState: TabsState = {
    activeKeys: {},
    activeTitles: [],
};

/**
 * State for tab blocks
 */
export const tabsAtom = atom<TabsState>({
    key: 'tabsAtom',
    default: defaultTabsState,
    effects: [localStorage.recoilEffect('@recoil/tabsAtom', defaultTabsState)],
});

/**
 * Return an API to handle tabs.
 */
export function useTabsState(
    tabsBlock: Block
): [selectedTab: Block, setSelectedTab: (tab: Block) => void] {
    const { sections: sectionIDs, selected: selectedSection } =
        React.useContext(EditorSectionIDsContext);

    // Prefilter all the section IDs for each tab into a map for easy access.
    const sectionIDsByTab = React.useMemo(() => {
        const sectionIDsForAllTabs = new Map<string, SectionIDs>();
        tabsBlock.blockNodes.forEach((block) => {
            const tabMap = createSectionIDs();
            [...sectionIDs.entries()]
                .filter(([key]) => block.getNode(key))
                .forEach(([key, id]) => {
                    tabMap.set(key, id);
                });
            sectionIDsForAllTabs.set(block.key, tabMap);
        });

        return sectionIDsForAllTabs;
    }, [sectionIDs, tabsBlock]);

    const [tabs, setTabs] = useRecoilState(tabsAtom);
    // This prioritize state makes sue that we will prioritize the selected section when it changes,
    // up to when the user will select a tab or removes the selected section.
    /**
     * Do NOT include `sectionIDsByTab` in the dependencies!
     * Doing so causes `prioritizeState` to be reset to `false` whenever `sectionIDsByTab` changes
     * and that happens when `tabsBlock` changes as per the `useMemo` above. So, with prioritizeState
     * set to "false", the `getSelectedTabByUser` method returns `undefined` thereby the selected
     * tab returned from the hook is set to the first tab
     */
    const [prioritizeState, setPrioritizeState] = useStateWithDeps(false, [selectedSection]);
    const editor = useEditor();

    const selectTab = React.useCallback(
        (tab: Block) => {
            const title = tab.data.get('title');

            // When changing the tab as we navigate, prioritize the recoil state over the hash.
            setPrioritizeState(true);
            setTabs((prev) => ({
                activeKeys: {
                    ...prev.activeKeys,
                    [tabsBlock.key]: tab.key,
                },
                activeTitles: title
                    ? prev.activeTitles
                          .filter((t) => t !== title)
                          .concat([title])
                          .slice(-TITLES_MAX)
                    : prev.activeTitles,
            }));
        },
        [setPrioritizeState, setTabs, tabsBlock.key]
    );

    // Handle setting active tab based on hash changes
    const hashSectionId = useURLHashSectionId();
    const selectTabFromHash = React.useCallback(
        (sectionId: string) => {
            const tabKey = [...sectionIDs.entries()].find(([, id]) => id === sectionId)?.[0];
            const tabFromHash = tabsBlock.blockNodes.find((tab) => tab.key === tabKey);

            if (tabFromHash) {
                selectTab(tabFromHash);
            }
        },
        [sectionIDs, tabsBlock.blockNodes]
    );
    React.useEffect(() => {
        if (hashSectionId) {
            selectTabFromHash(hashSectionId);
        }
    }, [hashSectionId]);

    return React.useMemo(() => {
        const { value, readOnly } = editor;

        // Is one of the tabs selected during editing?
        // This is needed because the rest of the logic might return
        // a node that doesn't match the one that is actually being edited.
        // Example:
        // The last tab could be active because a tab with the same name has previously
        // been clicked while the focus is in the first tab
        const selectedTab =
            (!readOnly ? getEditSelectedTab(value, tabsBlock) : false) ||
            // Is the last selected tab in this tab?
            getSelectedTabByUser(
                tabs,
                tabsBlock,
                prioritizeState,
                sectionIDsByTab,
                selectedSection
            ) ||
            // Or is there a block with the right title?
            (readOnly ? getMatchingTabByTitle(tabs, tabsBlock) : false) ||
            // Else, take the first block by default.
            tabsBlock.blockNodes.first()!;

        return [selectedTab, selectTab];
    }, [editor, tabs, setTabs, selectTab, prioritizeState, sectionIDsByTab, selectedSection]);
}

/**
 * Get explicitly selected tab in a set of tabs.
 */
function getSelectedTabByUser(
    state: TabsState,
    tabsBlock: Block,
    prioritizeState: boolean,
    sectionIDs: Map<string, SectionIDs>,
    selectedSection: string | null
): Block | undefined {
    const activeKey = state.activeKeys[tabsBlock.key];

    // Check if we have an active key in memory. If yes, use that. If not, check the hash
    // in the navigation screen. Always prioritize the hash unless told to prioritize the active
    // state.
    return prioritizeState && activeKey
        ? tabsBlock.blockNodes.find((child) => child.key === activeKey)
        : tabsBlock.blockNodes.find((child) =>
              [...(sectionIDs.get(child.key)?.values() || [])].includes(selectedSection || '')
          );
}

/**
 * When editing, return any tab that contains the selection.
 */
function getEditSelectedTab(value: Value, tabsBlock: Block): Block | undefined {
    if (!value.selection.isFocused) {
        return undefined;
    }
    return tabsBlock.blockNodes.find(
        (child) =>
            TextRange.isTextRange(value.selection) &&
            (value.selection.hasAnchorIn(child) || value.selection.hasFocusIn(child))
    );
}

/**
 * Get the best selected tab in a set of tabs by taking only title into account.
 */
function getMatchingTabByTitle(state: TabsState, tabsBlock: Block): Block | undefined {
    return (
        tabsBlock.blockNodes
            .map((node) => {
                const title = node.data.get('title');

                return {
                    node,
                    score: state.activeTitles.indexOf(title),
                };
            })
            .filter(({ score }) => score >= 0)
            .sortBy(({ score }) => -score)
            .map(({ node }) => node)
            .first() || undefined
    );
}
